import { useSuspenseQueries } from '@tanstack/react-query';

import * as api from '@gitbook/api-client';
import {
    assertNonLegacySiteType,
    checkIsFreeProductKey,
    checkIsPaidSiteProductKey,
    getSiteProductDefinition,
} from '@gitbook/app-pricing';
import { intlPluralizeString } from '@gitbook/react-uikit';

import { priceFormatter } from '../../../Components';
import {
    apiQuery,
    assertOrganizationWithBilling,
    getMonthlyMemberPrice,
    getMonthlySitePrice,
    useAssertCurrentOrganizationId,
} from '../../../state';
import { ChangeSitePlanBillingInterval } from './ChangeSitePlanBillingInterval';

export function SitePricingModalAside({ requiredSiteType }: { requiredSiteType: api.SiteType }) {
    assertNonLegacySiteType(requiredSiteType);

    const organizationId = useAssertCurrentOrganizationId();
    const [{ data: organization }, { data: membersUsage }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('getMembersUsageForOrganizationById', { organizationId }),
        ],
    });
    // The user has permission to publish/upgrade a site,
    // so they should have access to billing information.
    assertOrganizationWithBilling(organization);

    const siteMonthlyPrice = getMonthlySitePrice({
        siteType: requiredSiteType,
        billing: organization.billing,
    });
    const definition = getSiteProductDefinition(requiredSiteType);

    const isFreeOrgPlan = checkIsFreeProductKey(organization.plan);
    const memberMonthlyPrice = getMonthlyMemberPrice({
        product: api.BillingProduct.Pro2024,
        billing: organization.billing,
    });

    // Show total price (site + members) and price breakdown to orgs on Free selecting a paid site
    const showPriceBreakdown = isFreeOrgPlan && checkIsPaidSiteProductKey(requiredSiteType);
    const totalPrice = showPriceBreakdown
        ? siteMonthlyPrice + memberMonthlyPrice * membersUsage.members.total
        : siteMonthlyPrice;

    return (
        <div className="flex flex-col items-start gap-4">
            <div className="flex flex-col items-start">
                <span className="font-bold text-ui-page-title">{definition.title}</span>
                <div className="flex items-center gap-1">
                    <span className="text-ui-heading-medium">
                        {priceFormatter.format(totalPrice)}
                    </span>
                    <span className="text-muted text-ui-small"> /month</span>
                </div>
                {showPriceBreakdown ? (
                    <span className="text-muted text-ui-small">
                        {priceFormatter.format(siteMonthlyPrice)} +{' '}
                        {priceFormatter.format(memberMonthlyPrice)} x {membersUsage.members.total}{' '}
                        {intlPluralizeString({
                            count: membersUsage.members.total,
                            singular: 'user',
                            plural: 'users',
                        })}
                    </span>
                ) : null}
            </div>
            <ChangeSitePlanBillingInterval organization={organization} />
        </div>
    );
}
